/**
 * Platform admin navigation state: the active tab (synced with `?tab=`), the
 * Settings sub-section, and the "focused school" highlight used when a school
 * is opened in Settings > Billing from the Schools tab.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { normalizePlatformAdminTab } from '../platformAdminFormat';
import type { PlatformAdminTabKey, School, SettingsSection } from '../platformAdminTypes';

export function usePlatformAdminTabs() {
  const router = useRouter();
  const params = useLocalSearchParams<{ tab?: string | string[] }>();

  const normalizedTab = normalizePlatformAdminTab(params.tab);
  const [activeTab, setActiveTab] = useState<PlatformAdminTabKey>(normalizedTab);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('ai');
  const [focusedSchoolId, setFocusedSchoolId] = useState<number | null>(null);

  // Sync URL → tab. This effect must depend ONLY on `normalizedTab`. If we
  // also watch `activeTab`, every click on a tab triggers this effect (because
  // setActiveTab changes `activeTab` but the URL is unchanged, so
  // normalizedTab still points at the original tab) and immediately reverts
  // the click. The original feedback loop was the reason tabs didn't switch.
  useEffect(() => {
    setActiveTab(normalizedTab);
  }, [normalizedTab]);

  // Single helper for changing tabs: updates local state AND the URL so
  // refreshes / deep-links keep the user on the right tab.
  const handleTabChange = useCallback(
    (tab: PlatformAdminTabKey) => {
      setActiveTab(tab);
      try {
        router.setParams({ tab });
      } catch {
        // setParams isn't available outside expo-router; safe to swallow.
      }
    },
    [router],
  );

  useEffect(() => {
    if (focusedSchoolId == null) return;
    const timer = setTimeout(() => setFocusedSchoolId(null), 4000);
    return () => clearTimeout(timer);
  }, [focusedSchoolId]);

  useEffect(() => {
    if (activeTab !== 'settings' || focusedSchoolId == null) {
      return;
    }
    const targetId = `platform-admin-settings-card-${focusedSchoolId}`;
    const timeout = setTimeout(() => {
      const doc = (globalThis as any)?.document as any;
      if (!doc) return;
      const element =
        doc.getElementById(targetId) ||
        (doc.querySelector(`[data-nativeid="${targetId}"]`) as HTMLElement | null);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(timeout);
  }, [activeTab, focusedSchoolId]);

  const focusSchoolInSettings = (school: School) => {
    setFocusedSchoolId(school.id);
    setSettingsSection('billing');
    handleTabChange('settings');
  };

  return {
    activeTab,
    handleTabChange,
    settingsSection,
    setSettingsSection,
    focusedSchoolId,
    focusSchoolInSettings,
  };
}
